import { captureException } from "@sentry/vue";

import useVideoCallStore from "../Store";
import useChatStore from "@/Modules/Chat/Store";
import useUserStore from "@/Modules/User/Store";

import { not } from "@/Utils/Promises";
import { isValidString } from "@/Utils/String";
import { danger } from "@/Utils/Debug";
import { hasValue } from "@/Utils/Objects";
import { parseOrValidateInt } from "@/Utils/Numbers";

import { createRoom } from "@/Services/TherapyApi/VideoCalls";

import { hasPermissions, hasSupport } from "../Utils";

const StartVideoCallActionHandler = async (chatId: string, check: boolean = true): Promise<boolean> => {
  const videoCallStore = useVideoCallStore();
  const chatStore = useChatStore();
  const userStore = useUserStore();

  try {
    videoCallStore.clean();
    videoCallStore.isLoading = true;
    videoCallStore.showVideoCallError = false;
    videoCallStore.patientPermissionError = false;

    if (check && (!hasSupport() || (await not(hasPermissions())))) {
      videoCallStore.updateVideoCallError(true);
      videoCallStore.stopLoadingVideocall();

      return false;
    }

    if (!check || (await videoCallStore.isValidConfig())) {
      const response = await createRoom(chatId).catch(() => undefined);

      if (
        !hasValue(response) ||
        !isValidString(response.session_id) ||
        !isValidString(response.token) ||
        !isValidString(response.room_uuid)
      ) {
        videoCallStore.clean();

        return false;
      }

      if (response.free_sessions === -1) {
        videoCallStore.freeSessions = -1;

        return false;
      }

      videoCallStore.setupVideoCall({
        remoteUser: chatStore.rooms[chatId]!.user,
        sessionId: response.session_id,
        token: response.token,
        roomId: response.room_uuid,
        isTranscribed: response.is_transcription_enabled === true,
        isRatingRequired: response.is_rating_required === true,
        freeSessions: userStore.getUserIsTherapist
          ? parseOrValidateInt(response.free_sessions, undefined)
          : undefined,
      });

      return true;
    } else {
      videoCallStore.isLoading = false;
      videoCallStore.updateShowConfig(true);

      return false;
    }
  } catch (e) {
    danger(e);
    captureException(e);
    videoCallStore.clean();

    return false;
  }
};

export default StartVideoCallActionHandler;
